<template>
  <v-container fluid class="tasks-page">
    <v-row>
      <v-col cols="12">
        <v-card-title class="tasks-page__header d-flex justify-space-between align-center flex-wrap">
          <div class="d-flex align-center">
            <!-- <v-btn icon small class="mr-4" @click="$router.back()">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            -->
            <div>
              <div class="tasks-page__eyebrow">Tasks</div>
              <div class="tasks-page__title">Tasks</div>
              <div class="tasks-page__subtitle">Review tasks and update them side by side.</div>
            </div>
          </div>

          <div class="d-flex flex-wrap tasks-page__actions">
            <v-btn color="success" variant="outlined" dark small flat @click="exportToExcel" :loading="exporting" class="mr-3 tasks-page__action-btn">
              <v-icon left size="18">mdi-file-excel</v-icon>
              Export to Excel
            </v-btn>
            <v-btn color="success" dark small flat @click="onAddFinding" class="tasks-page__action-btn">
              <v-icon left size="18">mdi-plus</v-icon>
              Create Task
            </v-btn>
          </div>
        </v-card-title>
      </v-col>
    </v-row>

    <!-- Summary Stats Bar -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card variant="flat" color="grey-lighten-5" class="pa-3 tasks-summary-card">
          <div class="d-flex align-center flex-wrap tasks-summary-grid">
            <div class="task-stat">
              <div class="task-stat__icon primary">
                <v-icon color="primary">mdi-clipboard-list</v-icon>
              </div>
              <div>
                <div class="task-stat__value">{{ tasks.length }}</div>
                <div class="task-stat__label">Total</div>
              </div>
            </div>
            <div class="task-stat">
              <div class="task-stat__icon danger">
                <v-icon color="error">mdi-alert-circle</v-icon>
              </div>
              <div>
                <div class="task-stat__value">{{ overdueCount }}</div>
                <div class="task-stat__label">Overdue</div>
              </div>
            </div>
            <div class="task-stat">
              <div class="task-stat__icon warning">
                <v-icon color="warning">mdi-calendar-today</v-icon>
              </div>
              <div>
                <div class="task-stat__value">{{ dueTodayCount }}</div>
                <div class="task-stat__label">Due Today</div>
              </div>
            </div>
            <div class="task-stat">
              <div class="task-stat__icon success">
                <v-icon color="success">mdi-check-circle</v-icon>
              </div>
              <div>
                <div class="task-stat__value">{{ completedThisWeekCount }}</div>
                <div class="task-stat__label">Completed This Week</div>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Collapsible Filters / Sort Bar -->
    <v-row class="mt-2" dense>
      <v-col cols="12">
        <v-expansion-panels v-model="filterPanel" variant="accordion" class="mb-2 task-filter-panels">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-filter</v-icon>
                <span>Filters & Sort</span>
                <v-chip
                  v-if="activeFilterCount > 0"
                  size="x-small"
                  color="primary"
                  class="ml-2"
                >
                  {{ activeFilterCount }}
                </v-chip>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row dense>
                <v-col cols="12" md="8">
                  <v-row dense>
                    <!-- Status filter -->
                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="filters.status"
                        :items="statusOptions"
                        label="Filter by Status"
                        item-title="text"
                        item-value="value"
                        clearable
                        density="comfortable"
                      />
                    </v-col>

                    <!-- Priority filter -->
                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="filters.priority"
                        :items="priorityOptions"
                        label="Filter by Priority"
                        item-title="text"
                        item-value="value"
                        clearable
                        density="comfortable"
                      />
                    </v-col>

                    <!-- Zone filter -->
                    <v-col cols="12" sm="6" md="4">
                      <v-select
                        v-model="filters.zone"
                        :items="zoneOptions"
                        label="Filter by Zone"
                        item-title="text"
                        item-value="value"
                        clearable
                        density="comfortable"
                        prepend-inner-icon="mdi-map-marker"
                      />
                    </v-col>

                    <!-- Search (optional) -->
                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="filters.search"
                        label="Search title/description/assignee"
                        clearable
                        density="comfortable"
                        prepend-inner-icon="mdi-magnify"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="4">
                  <v-row dense>
                    <!-- Sort key -->
                    <v-col cols="12" sm="7">
                      <v-select
                        v-model="sort.key"
                        :items="sortKeyOptions"
                        label="Sort by"
                        density="comfortable"
                      />
                    </v-col>

                    <!-- Sort direction -->
                    <v-col cols="12" sm="5" class="d-flex align-center">
                      <v-btn-toggle
                        v-model="sort.dir"
                        mandatory
                        class="w-100"
                        density="comfortable"
                        style="position: relative; top: -8px"
                      >
                        <v-btn value="asc" variant="tonal">
                          <v-icon start>mdi-sort-ascending</v-icon> Asc
                        </v-btn>
                        <v-btn value="desc" variant="tonal">
                          <v-icon start>mdi-sort-descending</v-icon> Desc
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Active Filter Chips -->
        <div v-if="activeFilterCount > 0" class="d-flex align-center flex-wrap" style="gap: 8px">
          <v-chip
            v-if="filters.status"
            closable
            size="small"
            color="primary"
            variant="tonal"
            @click:close="filters.status = null"
          >
            Status: {{ getStatusLabel(filters.status) }}
          </v-chip>
          <v-chip
            v-if="filters.priority"
            closable
            size="small"
            color="primary"
            variant="tonal"
            @click:close="filters.priority = null"
          >
            Priority: {{ getPriorityLabel(filters.priority) }}
          </v-chip>
          <v-chip
            v-if="filters.search"
            closable
            size="small"
            color="primary"
            variant="tonal"
            @click:close="filters.search = ''"
          >
            Search: {{ filters.search }}
          </v-chip>
        </div>
      </v-col>
    </v-row>

    <!-- Main Split Layout -->
    <v-row>
      <!-- Left Column: Task List -->
      <v-col cols="12" md="5">
        <v-card elevation="1" class="task-list-card">
          <!-- Bulk Actions Toolbar -->
          <v-toolbar v-if="selectedTasks.length > 0" color="primary" density="compact" flat>
            <v-checkbox
              :model-value="allSelected"
              @update:model-value="toggleSelectAll"
              hide-details
              class="mr-2"
            />
            <span class="text-body-2">{{ selectedTasks.length }} selected</span>
            <v-spacer />
            <v-btn
              color="success"
              size="small"
              @click="onBulkComplete"
              :loading="bulkCompleting"
            >
              <v-icon start size="16">mdi-check-all</v-icon>
              Bulk Complete
            </v-btn>
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              @click="clearSelection"
            />
          </v-toolbar>

          <div class="task-list-card__header">
            <div>
              <div class="task-list-card__eyebrow">Task List</div>
              <div class="task-list-card__subtitle">{{ filteredSortedTasks.length }} shown</div>
            </div>
          </div>

          <v-list lines="two" subheader>
            <v-virtual-scroll
              :items="filteredSortedTasks"
              item-height="104"
              height="calc(100vh - 280px)"
            >
              <template #default="{ item, index }">
                <v-list-item
                  :key="item.taskId"
                  :value="item.taskId"
                  @click="selectTask(item)"
                  class="pa-3 row-sep task-item"
                  :class="{
                    'row-sep--last': index === filteredSortedTasks.length - 1,
                    'task-item--selected': String(selectedTask?.taskId ?? selectedTask?.TaskId ?? '') === String(item.taskId ?? item.TaskId ?? ''),
                  }"
                  :style="{ borderLeft: `3px solid ${item.priorityColour || '#ccc'}` }"
                >
                  <!-- Checkbox for bulk selection -->
                  <template #prepend>
                    <v-checkbox
                      :model-value="selectedTasks.includes(item.taskId)"
                      @click.stop
                      @update:model-value="(val) => toggleTaskSelection(item.taskId, val)"
                      hide-details
                      class="mr-2"
                    />
                  </template>
                  <!-- Row 1: Title + Status Badge -->
                  <div class="d-flex justify-space-between align-center w-100 mb-1">
                    <div class="task-item__title text-truncate" style="flex: 1">
                      {{ item.title }}
                    </div>
                    <v-chip
                      size="x-small"
                      :color="item.statusColour"
                      variant="flat"
                      class="ml-2 task-item__status-chip"
                      style="flex-shrink: 0"
                    >
                      {{ item.statusFK }}
                    </v-chip>
                  </div>

                  <div v-if="item.description" class="task-item__description text-truncate">
                    {{ item.description }}
                  </div>

                  <!-- Row 2: Zone + Due Date -->
                  <div class="task-item__meta text-grey-darken-1 mb-1">
                    <v-icon size="12" class="mr-1">mdi-map-marker</v-icon>
                    {{ item.zoneNameFK }}
                    <span class="mx-1">•</span>
                    <v-icon size="12" class="mr-1">mdi-calendar</v-icon>
                    Due: {{ formatDate(item.dueDate) }}
                  </div>

                  <!-- Row 3: Assigned + Linkage + Priority -->
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center min-w-0">
                      <v-avatar size="24" class="mr-2 bg-avatar">
                        <v-img :src="getUserIconUrl(item.assignedToIcon)" />
                      </v-avatar>
                      <span class="task-item__assignee text-truncate">{{ item.assignedToFK || "Unassigned" }}</span>
                    </div>
                    <div class="d-flex align-center task-item__actions" style="gap: 2px">
                      <v-tooltip v-if="item.findingId || item.FindingId" text="View Finding" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            size="x-small"
                            variant="text"
                            density="comfortable"
                            @click.stop="goToFinding(item)"
                          >
                            <v-icon size="14">mdi-file-find-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip v-if="item.assetId || item.AssetId" text="View Asset" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            size="x-small"
                            variant="text"
                            density="comfortable"
                            @click.stop="goToAsset(item)"
                          >
                            <v-icon size="14">mdi-cog-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-tooltip v-if="item.workOrderId || item.WorkOrderId" text="View Work Order" location="top">
                        <template #activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon
                            size="x-small"
                            variant="text"
                            density="comfortable"
                            @click.stop="goToWorkOrder(item)"
                          >
                            <v-icon size="14">mdi-clipboard-list-outline</v-icon>
                          </v-btn>
                        </template>
                      </v-tooltip>
                      <v-chip
                        size="x-small"
                        variant="outlined"
                        class="task-item__priority-chip"
                        :style="{ borderColor: item.priorityColour || '#ccc', color: item.priorityColour || '#666' }"
                      >
                        {{ item.priorityFK || "Priority" }}
                      </v-chip>
                    </div>
                  </div>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-list>
        </v-card>
      </v-col>

      <!-- Right Column: Task Detail -->
      <v-col cols="12" md="7">
        <TaskDetailPanel
          v-if="selectedTask"
          :task="selectedTask"
          :assignees="assignees"
          closable
          :key="selectedTask.taskId"
          :statuses="taskStatuses"
          :priorities="taskPriorities"
          @close="selectedTask = null"
          @request-edit="onEditTask"
          @completed="fetchTasks"
          @comment-posted="fetchTasks"
          @save="onSaveTask"
          @deleted="onTaskDeleted"
        />
        <!-- Placeholder -->
        <v-card v-else class="pa-8 text-center text-grey task-detail-placeholder">
          <v-icon size="48" class="mb-2">mdi-information-outline</v-icon>
          <div class="text-h6 text-medium-emphasis mb-2">Select a task to view details</div>
          <div class="text-body-2">The task summary, map, media and comments will appear here.</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Complete Task Dialog -->
    <v-dialog v-model="completeDialog.show" max-width="500px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span class="text-h6 font-weight-bold">Complete Task</span>
          <v-btn icon variant="text" @click="completeDialog.show = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Photo Upload -->
          <div class="mb-4">
            <label class="font-weight-medium">Add photo (Required)</label>
            <v-file-input
              :model-value="completePhotoFiles"
              prepend-icon="mdi-plus"
              accept="image/*"
              multiple
              show-size
              outlined
              dense
              @update:model-value="handlePhotoUpload"
            />
          </div>

          <!-- Completion Note -->
          <div>
            <label class="font-weight-medium">Add a completion note (Optional)</label>
            <v-textarea
              v-model="completeDialog.note"
              placeholder="Type here..."
              outlined
              dense
              rows="2"
            />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="outlined" @click="completeDialog.show = false">Cancel</v-btn>
          <v-btn
            color="green"
            variant="flat"
            :disabled="!hasCompletePhotos"
            @click="submitCompletion"
          >
            <v-icon start>mdi-check</v-icon> Mark Complete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { getAllTasks, getTasksByAssetId, getTasksByFindingId, getAllTaskComments, createTaskComment, updateTask, bulkCompleteTasks, exportTasksToExcel } from "@/api/tasks";
import { getUsers } from "@/api/users";
import { useAuthStore } from "@/stores/auth";
import { getPhotosByTaskId, uploadPhoto, reconcilePhotoSnapshots, revokePhotoSnapshots, snapshotsToFiles } from "@/api/photoItems";
import ZoneMapSimple from "/src/components/ZoneMapSimple.vue";
import TaskDetailPanel from "@/components/TaskDetailPanel.vue";
import { getLookupDataByType } from "@/api/lookups";
import { useDialogStore } from "@/stores/dialog";

export default {
  name: "TaskGridPage",
  setup() {
    const dialog = useDialogStore();
    return { dialog };
  },
  data() {
    return {
      tasks: [],
      selectedTask: null,
      selectedTasks: [], // For bulk selection (Feature #17)
      bulkCompleting: false,
      exporting: false,
      filterPanel: [], // Expansion panel state (empty = collapsed)
      filters: {
        status: null, // matches item.statusFK (text) or item.status (id) – we'll map to text
        priority: null, // matches item.priorityFK (text)
        zone: null, // matches item.zoneId
        search: "",
      },
      sort: {
        key: "dueDate", // default
        dir: "desc", // "asc" | "desc"
      },
      sortKeyOptions: [
        { title: "Assigned To", value: "assignedToFK" },
        { title: "Estimated Cost", value: "estimatedCost" },
        { title: "Due Date", value: "dueDate" },
        { title: "Priority", value: "priorityFK" }, // or "priority" if you prefer numeric
        { title: "Status", value: "statusFK" },
        { title: "Title", value: "title" },
        { title: "Zone", value: "zoneNameFK" },
      ],
      comments: [],
      newComment: "",
      loadingComments: false,
      media: [],
      lightbox: {
        show: false,
        img: null,
      },
      completeDialog: {
        show: false,
        snapshots: [],
        note: "",
      },
      assignees: [],
      taskStatuses: [],
      taskPriorities: [],
    };
  },
  components: { ZoneMapSimple, TaskDetailPanel },
  computed: {
    completePhotoFiles() {
      // Stable File[] derived from iOS-safe snapshots, bound into v-file-input.
      return snapshotsToFiles(this.completeDialog.snapshots);
    },
    hasCompletePhotos() {
      return this.completeDialog.snapshots.length > 0;
    },
    statusOptions() {
      // Unique list of status text (statusFK). Fallback to numeric status if no text.
      const set = new Map();
      for (const t of this.tasks) {
        const value = t.statusFK ?? String(t.status ?? "");
        const text = t.statusFK ?? `Status ${t.status ?? ""}`;
        if (value) set.set(value, { text, value });
      }
      return Array.from(set.values());
    },
    priorityOptions() {
      // Unique list of priority text (priorityFK). Fallback to numeric if no text.
      const set = new Map();
      for (const t of this.tasks) {
        const value = t.priorityFK ?? String(t.priority ?? "");
        const text = t.priorityFK ?? `Priority ${t.priority ?? ""}`;
        if (value) set.set(value, { text, value });
      }
      return Array.from(set.values());
    },
    zoneOptions() {
      // Unique list of zones present in the loaded tasks (value = zoneId, text = name).
      const set = new Map();
      for (const t of this.tasks) {
        const value = t.zoneId ?? "";
        if (value && !set.has(value)) {
          set.set(value, { text: t.zoneNameFK || String(value), value });
        }
      }
      return Array.from(set.values()).sort((a, b) => (a.text || "").localeCompare(b.text || ""));
    },

    filteredSortedTasks() {
      const s = (v) => (v ?? "").toString().toLowerCase();

      // 1) Filter - when we have assetId/findingId in query, we already fetched via specialized API, so skip that filter
      const assetIdFilter = this.$route?.query?.assetId ? parseInt(this.$route.query.assetId, 10) : null;
      const findingIdFilter = this.$route?.query?.findingId ?? null;
      const fetchedByAssetOrFinding = !!assetIdFilter || !!findingIdFilter;

      let list = this.tasks.filter((t) => {
        const passAssetId = fetchedByAssetOrFinding
          ? true
          : !assetIdFilter ||
            (t.assetId != null && Number(t.assetId) === assetIdFilter) ||
            (t.AssetId != null && Number(t.AssetId) === assetIdFilter);
        if (!passAssetId) return false;
        const passFindingId = fetchedByAssetOrFinding
          ? true
          : !findingIdFilter ||
            String(t.findingId ?? t.FindingId ?? "") === String(findingIdFilter);
        if (!passFindingId) return false;
        const passStatus =
          !this.filters.status ||
          (t.statusFK && t.statusFK === this.filters.status) ||
          String(t.status ?? "") === this.filters.status;

        const passPriority =
          !this.filters.priority ||
          (t.priorityFK && t.priorityFK === this.filters.priority) ||
          String(t.priority ?? "") === this.filters.priority;

        const passZone =
          !this.filters.zone || String(t.zoneId ?? "") === String(this.filters.zone);

        const q = s(this.filters.search);
        const passSearch =
          !q ||
          s(t.title).includes(q) ||
          s(t.description).includes(q) ||
          s(t.assignedToFK).includes(q) ||
          s(t.zoneNameFK).includes(q);

        return passStatus && passPriority && passZone && passSearch;
      });

      // 2) Sort
      const key = this.sort.key;
      const dir = this.sort.dir === "desc" ? -1 : 1;

      const getVal = (item) => {
        switch (key) {
          case "estimatedCost":
            return item.estimatedCost ?? 0;
          case "dueDate":
            return item.dueDate ? new Date(item.dueDate).getTime() : 0;
          case "priorityFK":
            // If you want numeric priority precedence, switch to item.priority
            return (item.priorityFK ?? "").toString();
          case "statusFK":
            return (item.statusFK ?? "").toString();
          case "assignedToFK":
            return (item.assignedToFK ?? "").toString();
          case "zoneNameFK":
            return (item.zoneNameFK ?? "").toString();
          case "title":
          default:
            return (item.title ?? "").toString();
        }
      };

      list = list.slice().sort((a, b) => {
        const va = getVal(a);
        const vb = getVal(b);

        // number/date vs string compare
        if (typeof va === "number" && typeof vb === "number") {
          return (va - vb) * dir;
        }
        return String(va).localeCompare(String(vb), undefined, { sensitivity: "base" }) * dir;
      });

      return list;
    },
    activeFilterCount() {
      let count = 0;
      if (this.filters.status) count++;
      if (this.filters.priority) count++;
      if (this.filters.search) count++;
      return count;
    },
    overdueCount() {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return this.tasks.filter((t) => {
        if (!t.dueDate) return false;
        const due = new Date(t.dueDate);
        due.setHours(0, 0, 0, 0);
        return due < now && t.statusFK?.toLowerCase() !== "completed" && t.statusFK?.toLowerCase() !== "resolved";
      }).length;
    },
    dueTodayCount() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return this.tasks.filter((t) => {
        if (!t.dueDate) return false;
        const due = new Date(t.dueDate);
        due.setHours(0, 0, 0, 0);
        return due >= today && due < tomorrow;
      }).length;
    },
    completedThisWeekCount() {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      weekAgo.setHours(0, 0, 0, 0);
      return this.tasks.filter((t) => {
        const status = t.statusFK?.toLowerCase() || "";
        return (status === "completed" || status === "resolved") && 
               t.lastUpdated && 
               new Date(t.lastUpdated) >= weekAgo;
      }).length;
    },
    allSelected() {
      if (this.filteredSortedTasks.length === 0) return false;
      return this.filteredSortedTasks.every(task => 
        this.selectedTasks.includes(task.taskId)
      );
    },
  },
  watch: {
    "$route.query.assetId"() {
      this.fetchTasks();
    },
    "$route.query.findingId"() {
      this.fetchTasks();
    },
    center(newVal) {
      if (this.map && newVal) {
        this.map.setView(newVal, this.zoom);
      }
    },
    marker(newVal) {
      if (this.markerInstance && newVal) {
        this.markerInstance.setLatLng(newVal);
      }
    },
  },
  methods: {
    async exportToExcel() {
      this.exporting = true;
      try {
        const response = await exportTasksToExcel();
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Tasks_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$toast?.success?.("Tasks exported to Excel successfully");
      } catch (error) {
        console.error("Export error:", error);
        this.$toast?.error?.("Failed to export tasks to Excel");
      } finally {
        this.exporting = false;
      }
    },
    async fetchTasks() {
      try {
        const assetId = this.$route?.query?.assetId;
        const findingId = this.$route?.query?.findingId;
        if (assetId) {
          const id = parseInt(assetId, 10);
          this.tasks = !isNaN(id) ? await getTasksByAssetId(id) : [];
        } else if (findingId) {
          this.tasks = await getTasksByFindingId(findingId) || [];
        } else {
          this.tasks = await getAllTasks();
        }
      } catch (err) {
        this.tasks = [];
      }
    },
    async fetchUsers() {
      try {
        this.assignees = await getUsers();
      } catch (err) {
      }
    },
    async selectTask(task) {
      this.selectedTask = task;

      // Fetch media for task (always by taskId - task photos only)
      if (task) {
        const taskId = task.taskId ?? task.TaskId;
        if (taskId) {
          this.media = await getPhotosByTaskId(taskId);
        } else {
          this.media = [];
        }
      } else {
        this.media = [];
      }
    },
    async onSaveTask(payload) {
      try {
        await this.fetchTasks();
        // reselect updated task
        const updated = this.tasks.find((t) => String(t.taskId ?? t.TaskId) === String(payload.taskId));
        if (updated) {
          this.selectedTask = updated;
        }
      } catch (e) {
      }
    },
    async onTaskDeleted() {
      // Clear selection and refresh task list
      this.selectedTask = null;
      await this.fetchTasks();
    },

    openLightbox(imgUrl) {
      this.lightbox.img = imgUrl;
      this.lightbox.show = true;
    },
    formatDateTime(val) {
      if (!val) return "-";
      const d = typeof val === "string" || typeof val === "number" ? new Date(val) : val;
      if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(d);
    },
    formatDate(val) {
      if (!val) return "-";
      const d = typeof val === "string" || typeof val === "number" ? new Date(val) : val;
      if (!(d instanceof Date) || isNaN(d.getTime())) return "-";
      return new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
      }).format(d);
    },
    getPriorityColor(priority) {
      switch (priority?.toLowerCase()) {
        case "high":
          return "red ";
        case "medium":
          return "orange";
        case "low":
          return "green";
        default:
          return "grey";
      }
    },
    onAddFinding() {
      // Navigate to task creation page
      this.$router.push({ name: "TaskCreate" });
    },
    getUserIconUrl(id) {
      // default to 1 if invalid, pad to two digits
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    goToFinding(item) {
      const id = item?.findingId ?? item?.FindingId;
      if (id) this.$router.push({ name: "FindingEdit", params: { id } });
    },
    goToAsset(item) {
      const id = item?.assetId ?? item?.AssetId;
      if (id) this.$router.push({ path: "/assets", query: { assetId: id } });
    },
    goToWorkOrder(item) {
      const id = item?.workOrderId ?? item?.WorkOrderId;
      if (id) this.$router.push({ name: "WorkOrder", params: { workOrderId: id } });
    },

    async submitComment() {
      const content = this.newComment.trim();
      if (!content) return;
      try {
        const newCommentDto = {
          commentId: "",
          taskId: this.selectedTask.taskId,
          comment: content,
          userId: useAuthStore().user?.profile?.sub || null,
        };

        await createTaskComment(newCommentDto);
        this.newComment = "";
        //await this.fetchComments(this.selectedTask.taskId);
      } catch (error) {
      }
    },
    openCompleteDialog() {
      if (!this.selectedTask) return;
      revokePhotoSnapshots(this.completeDialog.snapshots);
      this.completeDialog = { show: true, snapshots: [], note: "" };
    },

    async handlePhotoUpload(val) {
      // iOS-safe: snapshot bytes into memory immediately on @change so iOS
      // can't evict the camera-temp file before submit.
      this.completeDialog.snapshots = await reconcilePhotoSnapshots(this.completeDialog.snapshots, val);
    },

    async submitCompletion() {
      if (!this.selectedTask) return;
      const snaps = this.completeDialog.snapshots;
      if (!snaps.length) return;

      try {
        const payloadFor = (snap) => ({
          Blob: snap.blob,
          PhotoUrlId: crypto.randomUUID(),
          CreatedAt: new Date().toISOString(),
          ...(this.selectedTask.findingId
            ? { FindingId: this.selectedTask.findingId }
            : { TaskId: this.selectedTask.taskId }),
        });
        await Promise.all(snaps.map((snap) => uploadPhoto(payloadFor(snap))));

        const updated = {
          ...this.selectedTask,
          comment: this.completeDialog.note,
          status: "3",
        };
        await updateTask(updated);

        revokePhotoSnapshots(this.completeDialog.snapshots);
        this.completeDialog.snapshots = [];
        this.completeDialog.show = false;
        this.fetchTasks();
      } catch (err) {
        this.$toast?.error?.(err?.response?.data?.message || err?.message || "Failed to complete task");
      }
    },
    async loadLookups() {
      try {
        const [statuses, priorities] = await Promise.all([
          getLookupDataByType("TaskStatus"),
          getLookupDataByType("TaskPriority")
        ]);
        
        const toId = (item) => {
          const v = item.value != null && item.value !== "" ? parseInt(item.value, 10) : NaN;
          return Number.isFinite(v) ? v : item.lookupDataId;
        };
        this.taskStatuses = (statuses || [])
          .filter(item => item.isActive)
          .map(item => ({
            StatusId: toId(item),
            Status: item.name,
            statusId: toId(item),
            status: item.name,
            Colour: item.colour || ""
          }));
        this.taskPriorities = (priorities || [])
          .filter(item => item.isActive)
          .map(item => ({
            PriorityId: toId(item),
            Priority: item.name,
            priorityId: toId(item),
            priority: item.name,
            Colour: item.colour || ""
          }));
        
      } catch (e) {
        this.taskStatuses = [];
        this.taskPriorities = [];
      }
    },
    getStatusLabel(value) {
      const option = this.statusOptions.find((opt) => opt.value === value);
      return option?.text || value;
    },
    getPriorityLabel(value) {
      const option = this.priorityOptions.find((opt) => opt.value === value);
      return option?.text || value;
    },
    toggleTaskSelection(taskId, selected) {
      if (selected) {
        if (!this.selectedTasks.includes(taskId)) {
          this.selectedTasks.push(taskId);
        }
      } else {
        this.selectedTasks = this.selectedTasks.filter(id => id !== taskId);
      }
    },
    toggleSelectAll(selected) {
      if (selected) {
        this.selectedTasks = this.filteredSortedTasks
          .filter(task => task.status !== 3 && task.status !== 4) // Only select incomplete tasks
          .map(task => task.taskId);
      } else {
        this.selectedTasks = [];
      }
    },
    clearSelection() {
      this.selectedTasks = [];
    },
    async onBulkComplete() {
      if (this.selectedTasks.length === 0) return;

      const result = await this.dialog.confirm({
        title: "Complete Tasks",
        message: `Complete ${this.selectedTasks.length} task(s)?`,
        variant: "info",
        buttons: "ok-cancel"
      });
      if (result !== "ok") return;

      this.bulkCompleting = true;
      try {
        const result = await bulkCompleteTasks(this.selectedTasks);
        this.$toast?.success?.(
          `Successfully completed ${result.successCount} task(s).` +
          (result.failureCount > 0 ? ` ${result.failureCount} failed.` : "")
        );
        this.selectedTasks = [];
        await this.fetchTasks(); // Refresh task list
      } catch (err) {
        this.$toast?.error?.("Failed to complete tasks. " + (err.message || ""));
      } finally {
        this.bulkCompleting = false;
      }
    },
  },
  mounted() {
    this.fetchTasks();
    this.fetchUsers();
    this.loadLookups();
  },
  beforeUnmount() {
    revokePhotoSnapshots(this.completeDialog.snapshots);
  },
};
</script>

<style scoped>
.tasks-page {
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 38%);
}

.tasks-page__header {
  padding: 0 0 8px;
  gap: 16px;
}

.tasks-page__eyebrow {
  font-size: 0.88rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b7280;
  font-weight: 600;
}

.tasks-page__title {
  font-size: 2rem;
  line-height: 1.1;
  font-weight: 700;
  color: #1f2937;
}

.tasks-page__subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 0.98rem;
}

.tasks-page__actions {
  gap: 12px;
}

.tasks-page__action-btn {
  border-radius: 12px;
  min-height: 40px;
}

.tasks-summary-card {
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.96), #ffffff);
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.06);
}

.tasks-summary-grid {
  gap: 18px;
}

.task-stat {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  min-width: 190px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.9);
}

.task-stat__icon {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-stat__icon.primary {
  background: rgba(37, 99, 235, 0.1);
}

.task-stat__icon.danger {
  background: rgba(239, 68, 68, 0.1);
}

.task-stat__icon.warning {
  background: rgba(245, 158, 11, 0.12);
}

.task-stat__icon.success {
  background: rgba(34, 197, 94, 0.12);
}

.task-stat__value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1;
}

.task-stat__label {
  margin-top: 4px;
  font-size: 0.84rem;
  color: #64748b;
}

.task-filter-panels :deep(.v-expansion-panel) {
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.05);
}

.task-list-card {
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.task-list-card__header {
  padding: 18px 20px 12px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.9), rgba(255, 255, 255, 0.98));
}

.task-list-card__eyebrow {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #64748b;
}

.task-list-card__subtitle {
  margin-top: 4px;
  font-size: 0.92rem;
  color: #475569;
}

.v-list-item {
  cursor: pointer;
}

::v-deep(.map-container.leaflet-container.leaflet-touch) {
  height: 250px;
  margin-bottom: 45px;
}

.row-sep {
  position: relative;
  box-shadow: inset 0 -1px rgba(226, 232, 240, 0.85);
}

.row-sep--last {
  box-shadow: none;
}

.task-item {
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  min-height: 104px;
}

.task-item:hover {
  background-color: rgba(248, 250, 252, 0.95);
}

.task-item--selected {
  background: linear-gradient(180deg, rgba(239, 246, 255, 0.95), rgba(248, 250, 252, 0.98));
  box-shadow: inset 0 0 0 1px rgba(96, 165, 250, 0.35);
}

.task-item__title {
  font-size: 1.12rem;
  font-weight: 700;
  color: #1f2937;
}

.task-item__status-chip {
  font-weight: 600;
}

.task-item__description {
  margin-bottom: 6px;
  font-size: 0.84rem;
  color: #64748b;
}

.task-item__meta {
  font-size: 0.83rem;
}

.task-item__assignee {
  max-width: 132px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.min-w-0 {
  min-width: 0;
}

.task-item__actions {
  flex-shrink: 0;
}

.task-item__priority-chip {
  margin-left: 6px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.8);
}

.task-detail-placeholder {
  border-radius: 22px;
  border: 1px dashed rgba(148, 163, 184, 0.45);
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.92), rgba(255, 255, 255, 0.98));
}

@media (max-width: 960px) {
  .tasks-page__title {
    font-size: 1.6rem;
  }

  .task-stat {
    min-width: 160px;
  }
}
</style>
